import { useState, useEffect, useCallback, useMemo } from "react";
import { FONT_THEMES, DEFAULT_THEME_ID, FONT_THEME_STORAGE_KEY } from "../themes/fontThemes";
import type { FontTheme } from "../themes/fontThemes";

const GOOGLE_FONTS_LINK_ID = "font-theme-google-fonts";
const SELF_HOSTED_STYLE_ID = "font-theme-self-hosted";

function applyTheme(theme: FontTheme): void {
  const root = document.documentElement;
  root.style.setProperty("--font-sans", theme.sans);
  root.style.setProperty("--font-mono", theme.mono);

  // Google Fonts <link>
  const existingLink = document.getElementById(GOOGLE_FONTS_LINK_ID) as HTMLLinkElement | null;
  if (theme.googleFontsUrl) {
    if (existingLink) {
      if (existingLink.href !== theme.googleFontsUrl) {
        existingLink.href = theme.googleFontsUrl;
      }
    } else {
      const link = document.createElement("link");
      link.id = GOOGLE_FONTS_LINK_ID;
      link.rel = "stylesheet";
      link.href = theme.googleFontsUrl;
      document.head.appendChild(link);
    }
  } else if (existingLink) {
    existingLink.remove();
  }

  // Self-hosted font CSS (@font-face)
  const existingStyle = document.getElementById(SELF_HOSTED_STYLE_ID);
  if (theme.selfHostedFontCss) {
    if (existingStyle) {
      if (existingStyle.textContent !== theme.selfHostedFontCss) {
        existingStyle.textContent = theme.selfHostedFontCss;
      }
    } else {
      const style = document.createElement("style");
      style.id = SELF_HOSTED_STYLE_ID;
      style.textContent = theme.selfHostedFontCss;
      document.head.appendChild(style);
    }
  } else if (existingStyle) {
    existingStyle.remove();
  }
}

export function useFontTheme() {
  const [themeId, setThemeIdState] = useState<string>(() => {
    try {
      const saved = localStorage.getItem(FONT_THEME_STORAGE_KEY);
      return saved && FONT_THEMES[saved] ? saved : DEFAULT_THEME_ID;
    } catch {
      return DEFAULT_THEME_ID;
    }
  });

  useEffect(() => {
    const theme = FONT_THEMES[themeId] || FONT_THEMES[DEFAULT_THEME_ID];
    applyTheme(theme);
  }, [themeId]);

  const setTheme = useCallback((id: string) => {
    if (!FONT_THEMES[id]) return;
    setThemeIdState(id);
    try {
      localStorage.setItem(FONT_THEME_STORAGE_KEY, id);
    } catch {
      // ignore
    }
  }, []);

  // Sync from server — call this when account data loads
  const syncFromServer = useCallback((serverThemeId: string | null | undefined) => {
    const id = serverThemeId && FONT_THEMES[serverThemeId] ? serverThemeId : DEFAULT_THEME_ID;
    setThemeIdState(id);
    try {
      localStorage.setItem(FONT_THEME_STORAGE_KEY, id);
    } catch {
      // ignore
    }
  }, []);

  const themes = useMemo(() => Object.values(FONT_THEMES), []);

  return { themeId, setTheme, syncFromServer, themes };
}
